import { SignInErrorCodeV1, SignInErrorV1 } from '@vista-digital/ocapi-client';
import { MemberAuthenticationGrantType } from '@vista-digital/ocapi-types/v1';
import { ocapiClient } from '../../../../../api-clients/ocapi/client';
import { configurationSettings } from '../../../../../configuration/configuration-settings';
import { LoyaltyAuthenticationType } from '../../../../../configuration/types/loyalty/loyalty-authentication-type';
import { LoyaltyMemberIdentifier } from '../../../../../configuration/types/loyalty/loyalty-member-identifier';
import { CommonErrorCodes } from '../../../../../types/errors/common-error-codes';
import { logger } from '../../../../../utils/logger';
import { nonEmptyStringValidator } from '../../../../../utils/validators/string';
import { clearCxmData } from '../../../../browsing/actions/cxm/clear-cxm-data';
import { LoyaltyDomainError, LoyaltyDomainSpecificErrorCode } from '../../../types/error';
import { loadMember } from '../load-member';
import { updateIsMemberSignedInFromCookie } from './update-is-member-signed-in-from-cookie';
import { updateIsMemberSignedInFromToken } from './update-is-member-signed-in-from-token';
/**
 * Sign in a loyalty member. This will either create a new cookie session or retrieve an [[AuthorizationToken]]
 * depending on the [[LoyaltyAuthenticationConfig]].
 */
export const signMemberIn = async (request, captchaResponse) => {
    try {
        await signInApi(request, captchaResponse);
    }
    catch (error) {
        throw handleSignInApiError(error);
    }
    await loadMember();
    clearCxmData();
};
const signInApi = async (request, captchaResponse) => {
    const { authentication } = configurationSettings.data.loyalty;
    if (request.grant_type === MemberAuthenticationGrantType.Password) {
        const memberIdentifier = authentication.memberIdentifier === LoyaltyMemberIdentifier.Email ? request.email : request.username;
        if (!memberIdentifier || !nonEmptyStringValidator(memberIdentifier, logger.error, 'signIn')) {
            throw new Error('Cannot sign in without a loyalty member identifier');
        }
    }
    switch (authentication.type) {
        case LoyaltyAuthenticationType.Token: {
            if (isPasswordRequest(request)) {
                const token = await ocapiClient.instance.v1.loyalty.members.authentication.createToken(request, captchaResponse);
                await updateIsMemberSignedInFromToken(token);
            }
            return;
        }
        case LoyaltyAuthenticationType.Cookie: {
            if (isPasswordRequest(request)) {
                await ocapiClient.instance.v1.loyalty.members.authentication.createCookie(request, captchaResponse);
                updateIsMemberSignedInFromCookie(true); // Must update isMemberSignedIn before loading the member, as the loadMember action requires isMemberSignedIn === true.
            }
            return;
        }
        case LoyaltyAuthenticationType.WorkstationCookie: {
            await ocapiClient.instance.v1.loyalty.members.authentication.createWorkstationCookie(request, captchaResponse);
            updateIsMemberSignedInFromCookie(true); // Must update isMemberSignedIn before loading the member, as the loadMember action requires isMemberSignedIn === true.
            return;
        }
        default: {
            throw new Error('Unknown LoyaltyAuthenticationType');
        }
    }
};
const isPasswordRequest = (request) => {
    switch (request.grant_type) {
        case MemberAuthenticationGrantType.DeviceToken:
            throw new Error('Cannot sign in member. Requires LoyaltyAuthenticationType.WorkstationCookie.');
        case MemberAuthenticationGrantType.Password:
            return true;
        default:
            return false;
    }
};
const handleSignInApiError = (error) => {
    if (!(error instanceof SignInErrorV1))
        throw error; // Unexpected error, rethrow immediately
    const errorCode = convertApiToDomainErrorCode(error.errorCode);
    return new LoyaltyDomainError(errorCode, error.message);
};
const convertApiToDomainErrorCode = (errorCode) => {
    switch (errorCode) {
        case SignInErrorCodeV1.InvalidCredentials:
            return LoyaltyDomainSpecificErrorCode.InvalidCredentials;
        case SignInErrorCodeV1.MemberLocked:
            return LoyaltyDomainSpecificErrorCode.MemberLocked;
        case SignInErrorCodeV1.DeviceLocked:
            return LoyaltyDomainSpecificErrorCode.DeviceLocked;
        default:
            return CommonErrorCodes.UnknownError;
    }
};
//# sourceMappingURL=sign-member-in.js.map